import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import toast from 'react-hot-toast';
import { TranslatedText } from '@medical/shared/components/TranslatedText';

const DistressHistoryContainer = styled.div`
  max-width: ${props => props.compact ? '100%' : '1200px'};
  margin: ${props => props.compact ? '0' : '0 auto'};
  padding: ${props => props.compact ? '0' : '2rem 1rem'};

  @media (max-width: 768px) {
    padding: ${props => props.compact ? '0' : '1rem 0.5rem'};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  margin: 0;
`;

const ControlsSection = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLabel = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.875rem;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DateInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.875rem;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const RefreshButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DistressList = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const DistressHeader = styled.div`
  background: #f8fafc;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DistressTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const DistressItem = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DistressItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DistressScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const DistressScore = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DistressBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  background: ${props => {
    if (props.score >= 8) return '#fee2e2';
    if (props.score >= 4) return '#fef3c7';
    if (props.score >= 1) return '#dbeafe';
    return '#d1fae5';
  }};
  color: ${props => {
    if (props.score >= 8) return '#991b1b';
    if (props.score >= 4) return '#92400e';
    if (props.score >= 1) return '#1e40af';
    return '#065f46';
  }};
  border: 2px solid ${props => {
    if (props.score >= 8) return '#fecaca';
    if (props.score >= 4) return '#fde68a';
    if (props.score >= 1) return '#bfdbfe';
    return '#a7f3d0';
  }};
`;

const DistressTime = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const DistressDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DistressSources = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SourceTag = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
`;

const DistressNotes = styled.div`
  background: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  border-left: 3px solid #3b82f6;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
`;

const HighDistressAlert = styled.div`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 1rem;
`;

const DistressHistory = ({ patientId = null, compact = false }) => {
  const [distressLogs, setDistressLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    minScore: '',
    startDate: '',
    endDate: ''
  });

  const fetchDistressLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('doctorToken') || localStorage.getItem('token');
      const params = {};
      
      if (filters.minScore) {
        params.minScore = parseInt(filters.minScore);
      }
      if (filters.startDate) {
        params.startDate = new Date(filters.startDate).toISOString();
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        params.endDate = endDate.toISOString();
      }

      const url = patientId 
        ? `/api/distress-logs/patient/${patientId}`
        : '/api/distress-logs';
      
      const response = await axios.get(url, {
        params: {
          ...params,
          limit: 100
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const logs = response.data.distressLogs || [];
        setDistressLogs(logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      } else {
        throw new Error(response.data.message || 'Failed to fetch distress logs');
      }
    } catch (err) {
      console.error('Error fetching distress logs:', err);
      setError(err.response?.data?.message || 'Failed to load distress logs');
      toast.error('Failed to load distress logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistressLogs();
  }, [patientId, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getDistressLabel = (score) => {
    if (score >= 8) return 'Extreme Distress';
    if (score >= 4) return 'Moderate to Severe Distress';
    if (score >= 1) return 'Mild Distress';
    return 'No Distress';
  };

  const calculateStats = () => {
    if (distressLogs.length === 0) return null;

    const scores = distressLogs.map(log => log.distressScore);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    const maxScore = Math.max(...scores);
    const minScore = Math.min(...scores);
    const highDistressCount = distressLogs.filter(log => log.distressScore >= 4).length;

    return {
      total: distressLogs.length,
      average: avgScore.toFixed(1),
      max: maxScore,
      min: minScore,
      highDistress: highDistressCount
    };
  };

  const stats = calculateStats();

  if (loading) {
    return <LoadingSpinner text="Loading distress history..." />;
  }

  if (error) {
    return (
      <DistressHistoryContainer compact={compact}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>Error loading distress history</h2>
          <p>{error}</p>
          <RefreshButton onClick={fetchDistressLogs}>Try Again</RefreshButton>
        </div>
      </DistressHistoryContainer>
    );
  }

  return (
    <DistressHistoryContainer compact={compact}>
      {!compact && (
        <Header>
          <Title>Distress History</Title>
          <Subtitle>Track patient distress levels over time</Subtitle>
        </Header>
      )}

      <ControlsSection>
        <FilterGroup>
          <FilterLabel>Minimum Score:</FilterLabel>
          <Select
            value={filters.minScore}
            onChange={(e) => handleFilterChange('minScore', e.target.value)}
          >
            <option value="">All Scores</option>
            <option value="0">0+</option>
            <option value="4">4+ (Clinically Significant)</option>
            <option value="7">7+ (High)</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Start Date:</FilterLabel>
          <DateInput
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>End Date:</FilterLabel>
          <DateInput
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </FilterGroup>

        <RefreshButton onClick={() => setFilters({ minScore: '', startDate: '', endDate: '' })}>
          Clear Filters
        </RefreshButton>
      </ControlsSection>

      {stats && (
        <StatsSection>
          <StatCard>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Entries</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.average}</StatValue>
            <StatLabel>Average Score</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.max}</StatValue>
            <StatLabel>Highest Score</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue style={{ color: stats.highDistress > 0 ? '#ef4444' : '#10b981' }}>
              {stats.highDistress}
            </StatValue>
            <StatLabel>High Distress (≥4)</StatLabel>
          </StatCard>
        </StatsSection>
      )}

      {distressLogs.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🌡️</EmptyIcon>
          <EmptyTitle>No distress logs found</EmptyTitle>
          <EmptyText>
            {filters.minScore || filters.startDate || filters.endDate
              ? 'No logs match your filter criteria'
              : 'No distress logs have been recorded yet'}
          </EmptyText>
        </EmptyState>
      ) : (
        <DistressList>
          <DistressHeader>
            <DistressTitle>Distress Logs ({distressLogs.length})</DistressTitle>
            <RefreshButton onClick={fetchDistressLogs}>Refresh</RefreshButton>
          </DistressHeader>
          {distressLogs.map((log) => (
            <DistressItem key={log._id}>
              <DistressItemHeader>
                <DistressScoreContainer>
                  <DistressScore>
                    <span style={{ fontWeight: 600, color: '#374151' }}>Distress Score:</span>
                    <DistressBadge score={log.distressScore}>
                      {log.distressScore}/10
                    </DistressBadge>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      ({getDistressLabel(log.distressScore)})
                    </span>
                  </DistressScore>
                </DistressScoreContainer>
                <DistressTime>
                  {format(new Date(log.timestamp || log.createdAt), 'MMM dd, yyyy • h:mm a')}
                </DistressTime>
              </DistressItemHeader>
              
              <DistressDetails>
                {log.distressSources && log.distressSources.length > 0 && (
                  <div>
                    <strong style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.5rem' }}>
                      Sources of Distress:
                    </strong>
                    <DistressSources>
                      {log.distressSources.map((source, index) => (
                        <SourceTag key={index}>
                          <TranslatedText text={source} />
                        </SourceTag>
                      ))}
                    </DistressSources>
                  </div>
                )}
                
                {log.distressNotes && (
                  <DistressNotes>
                    <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#111827' }}>
                      Notes:
                    </strong>
                    <TranslatedText text={log.distressNotes} />
                  </DistressNotes>
                )}
              </DistressDetails>

              {log.distressScore >= 4 && (
                <HighDistressAlert>
                  🚨 Clinically Significant Distress - Immediate attention recommended
                </HighDistressAlert>
              )}
            </DistressItem>
          ))}
        </DistressList>
      )}
    </DistressHistoryContainer>
  );
};

export default DistressHistory;

